import { useCallback, useState } from 'react';
import { Photo, SBL_PHOTO_CART_ITEMS, SBL_PHOTO_PURCHASE_IN_PROGRESS, usePhotoGalleryContainer } from '..';
import styles from './styles/PhotoGalleryCart.module.scss';
import Button from 'components/Button';
import client from 'helpers/client';
import RightArrow from '../../../icons/RightArrow';
import { useUser } from 'stores/userStore';
import PhotoGalleryCartItems from './PhotoGalleryCartItems';
import PhotoGalleryCartTransition from './PhotoGalleryCartTransition';
import { localCache } from 'helpers/cache';
import PhotoGalleryCartLoading from './PhotoGalleryCartLoading';
import { DISCOUNT_MULTIPLIER, TRANSACTION_FEE_IN_CENTS } from './PhotoGalleryCartItems';
import { PhotoGalleryAlertTypeEnum } from './PhotoGalleryAlertModal';
import { useApplicationContainer } from 'containers/ApplicationContainer';

type BuyResponseType = {
  checkoutUrl: Location;
};

const DISCOUNT_PERCENTAGE = new Intl.NumberFormat('en-US', {
  style: 'percent'
}).format(1 - DISCOUNT_MULTIPLIER);

export default function PhotoGalleryCart() {
  const [{ baseUrl }, { handleOpenLoginModal, rewriteUrl, updateCurrentQueryStringParams }] = useApplicationContainer();
  const [{ selectedPhotos, photoSlides }, { setAlertType, setShowCart }] = usePhotoGalleryContainer();
  const { isLoggedIn, account } = useUser();
  const isEmptyCart = selectedPhotos.length === 0;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const checkoutButtonLabel = isLoading ? <PhotoGalleryCartLoading /> : 'Continue to Checkout';

  const checkout = useCallback(async () => {
    setIsLoading(true);
    const photosForPurchase = selectedPhotos.map(photo => ({
      id: (photoSlides[photo.index] as Photo).id,
      isHighResolution: photo.isHighResolution
    }));
    const purchaseInfo = {
      customerEmail: account?.email ?? undefined,
      photos: photosForPurchase,
      successUrl: `${baseUrl}${rewriteUrl(updateCurrentQueryStringParams({ key: 'purchase', value: 'successful' }))}`,
      cancelUrl: `${baseUrl}${rewriteUrl(updateCurrentQueryStringParams({ key: 'purchase', value: 'cancel' }))}`
    };

    let response: BuyResponseType;
    try {
      response = await client.request({ path: '/photo_gallery/buy', method: 'post', data: purchaseInfo });
      localCache.set(SBL_PHOTO_PURCHASE_IN_PROGRESS, true);
      localCache.set(SBL_PHOTO_CART_ITEMS, selectedPhotos);
      window.location = response.checkoutUrl;
    } catch (e) {
      setIsLoading(false);
      setAlertType(PhotoGalleryAlertTypeEnum.PHOTO_UNAVAILABLE_FOR_PURCHASE);
    }
  }, [account, client, photoSlides, selectedPhotos]);

  return (
    <PhotoGalleryCartTransition>
      <>
        <div className={styles.CartClose} onClick={() => setShowCart(false)}>
          <RightArrow />
        </div>
        <div className={styles.CartHeader}>
          <div>Shopping cart</div>
          {!isEmptyCart ? (
            <div className={styles.CartSubtotal}>
              Subtotal $
              {(
                selectedPhotos.reduce((acc, cur) => {
                  const nextTotal = acc + (isLoggedIn ? DISCOUNT_MULTIPLIER * cur.price : cur.price);
                  return nextTotal;
                }, TRANSACTION_FEE_IN_CENTS) / 100
              ).toFixed(2)}
            </div>
          ) : null}
        </div>
        <div className={styles.CartHorizontalRule}></div>
        {/* 1. signed OUT and empty cart */}
        {!isLoggedIn && isEmptyCart ? (
          <div className={styles.CartEmpty}>
            <div className={styles.CartEmptyHeader}>Your cart is empty</div>
            <div className={styles.CartEmptyMessage}>
              Sign in or create a free account to get {DISCOUNT_PERCENTAGE} off photo <br />
              purchases and access to special members-only benefits.
            </div>
            <Button label="Sign In" type="v2" className={styles.CheckoutButton} onClick={handleOpenLoginModal} />
          </div>
        ) : null}
        {/* 2. signed IN and empty cart */}
        {isLoggedIn && isEmptyCart ? (
          <div className={styles.CartEmpty}>
            <div className={styles.CartEmptyHeader}>Your cart is empty</div>
            <div className={styles.CartEmptyMessage}>Photos added to your cart will be shown here</div>
            <Button label="Continue Shopping" type="v2" className={styles.CheckoutButton} onClick={() => setShowCart(false)} />
          </div>
        ) : null}
        {!isEmptyCart ? (
          <div className={styles.PhotoDisclaimer}>
            SBLive uses Stripe for secure payments. By completing a photo purchase, you agree to{' '}
            <span
              className={styles.PhotoLicenseAgreementLink}
              onClick={() => setAlertType(PhotoGalleryAlertTypeEnum.PHOTO_LICENSE_AGREEMENT)}
            >
              SBLive Sports' Non-Commercial Image License Agreement
            </span>
            .
          </div>
        ) : null}
        {/* 3. signed OUT and non-empty cart */}
        {!isLoggedIn && !isEmptyCart ? (
          <div className={styles.CartEmpty}>
            <Button label={checkoutButtonLabel} type="v2" className={styles.CheckoutButton} onClick={checkout} />
            <Button
              label={`Sign In For ${DISCOUNT_PERCENTAGE} Off`}
              type="v2"
              className={styles.CheckoutSignInButton}
              onClick={handleOpenLoginModal}
            />
            <PhotoGalleryCartItems />
          </div>
        ) : null}
        {/* 4. signed IN and non-empty cart */}
        {isLoggedIn && !isEmptyCart ? (
          <div className={styles.CartEmpty}>
            <Button label={checkoutButtonLabel} type="v2" className={styles.CheckoutButton} onClick={checkout} />
            <PhotoGalleryCartItems />
          </div>
        ) : null}
      </>
    </PhotoGalleryCartTransition>
  );
}
